// hooks/useSortingHook.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SortOrder = 'ascending' | 'descending';

export type SortOption = { key: string; label: string };

interface UseSortHookParams {
  tabId: string;
  initialSortKey?: string;
  initialSortOrder?: SortOrder;
  onUserSortChange?: (sort: { sortKey: string; sortOrder: SortOrder }) => void;
  /** Override the default Shipments sort options, so this hook can be reused by other pages (e.g. GxP reports). */
  sortOptions?: SortOption[];
}

export const useSortingHook = ({
  tabId,
  initialSortKey = 'scheduled_delivery_date',
  initialSortOrder = 'ascending',
  onUserSortChange,
  sortOptions: sortOptionsProp,
}: UseSortHookParams) => {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const lastTabIdRef = useRef(tabId);
  const lastInitialSortRef = useRef({
    tabId,
    sortKey: initialSortKey,
    sortOrder: initialSortOrder,
  });
  const hasUserChangedSortRef = useRef(false);

  useEffect(() => {
    const didTabChange = lastTabIdRef.current !== tabId;
    const didInitialSortChange =
      lastInitialSortRef.current.tabId !== tabId ||
      lastInitialSortRef.current.sortKey !== initialSortKey ||
      lastInitialSortRef.current.sortOrder !== initialSortOrder;

    lastInitialSortRef.current = {
      tabId,
      sortKey: initialSortKey,
      sortOrder: initialSortOrder,
    };

    if (
      !didTabChange &&
      (!didInitialSortChange || hasUserChangedSortRef.current)
    ) {
      return;
    }

    lastTabIdRef.current = tabId;
    hasUserChangedSortRef.current = false;
    setSortKey(initialSortKey);
    setSortOrder(initialSortOrder);
  }, [initialSortKey, initialSortOrder, tabId]);

  const selectedSortLabel = useMemo(
    () => sortOptionsProp.find((option) => option.key === sortKey)?.label ?? '',
    [sortKey, sortOptionsProp]
  );

  const handleSortChange = useCallback(
    (key: string) => {
      if (key === sortKey) return;

      hasUserChangedSortRef.current = true;
      setSortKey(key);
      onUserSortChange?.({ sortKey: key, sortOrder });
    },
    [onUserSortChange, sortKey, sortOrder]
  );

  const handleSortOrderToggle = useCallback(() => {
    setSortOrder((prev) => {
      const nextSortOrder = prev === 'ascending' ? 'descending' : 'ascending';
      hasUserChangedSortRef.current = true;
      onUserSortChange?.({ sortKey, sortOrder: nextSortOrder });
      return nextSortOrder;
    });
  }, [onUserSortChange, sortKey]);

  const sortMenu = useMemo(
    () => ({
      items: sortOptionsProp.map((option) => ({
        key: option.key,
        label: option.label,
        onClick: () => handleSortChange(option.key),
      })),
    }),
    [handleSortChange, sortOptionsProp]
  );

  return {
    sortKey,
    sortOrder,
    sortOptions: sortOptionsProp,
    sortMenu,
    selectedSortLabel,
    handleSortChange,
    handleSortOrderToggle,
  };
};
